import React, { useEffect, useRef, useState } from 'react';
import { Flag } from '../lib/flags';

// ── Pixel trophy (inline SVG, crisp edges) ──────────────────────────────────
// 'X' gold body, 'H' handles, '.' empty. Rendered as a grid of <rect>s.
const TROPHY = [
  'XXXXXXXXX',
  'X.......X',
  'X.......X',
  'HX.....XH',
  'HX.....XH',
  'H.X...X.H',
  '..XXXXX..',
  '....X....',
  '....X....',
  '...XXX...',
  '..XXXXX..',
  '.XXXXXXX.',
];
function PixelTrophy() {
  const cell = 7;
  const w = TROPHY[0].length, h = TROPHY.length;
  const rects = [];
  TROPHY.forEach((row, y) => {
    [...row].forEach((c, x) => {
      if (c === '.') return;
      const gold = c === 'X' ? '#FFD23A' : '#E0A100';
      rects.push(<rect key={`${x}-${y}`} x={x * cell} y={y * cell} width={cell} height={cell} fill={gold} />);
    });
  });
  return (
    <svg className="celebrate-trophy" width={w * cell} height={h * cell} viewBox={`0 0 ${w * cell} ${h * cell}`}
         style={{ shapeRendering: 'crispEdges' }} aria-hidden="true">
      {rects}
    </svg>
  );
}

// Short looping victory arpeggio via Web Audio. Created lazily on unmute so there's
// no autoplay (browsers block it anyway) — muted by default.
function useChiptune() {
  const ctxRef = useRef(null);
  const timerRef = useRef(null);
  const start = () => {
    if (ctxRef.current) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    ctxRef.current = ctx;
    const notes = [523.25, 659.25, 783.99, 1046.5, 880.0, 659.25];
    let i = 0;
    const tick = () => {
      const c = ctxRef.current;
      if (!c) return;
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = 'square';
      osc.frequency.value = notes[i % notes.length];
      gain.gain.setValueAtTime(0.0001, c.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.06, c.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.16);
      osc.connect(gain).connect(c.destination);
      osc.start();
      osc.stop(c.currentTime + 0.18);
      i++;
    };
    tick();
    timerRef.current = setInterval(tick, 200);
  };
  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (ctxRef.current) { ctxRef.current.close().catch(() => {}); ctxRef.current = null; }
  };
  useEffect(() => stop, []); // cleanup on unmount
  return { start, stop };
}

const CONFETTI_COLORS = ['#BFF73A', '#FF6A2C', '#FFD23A', '#3AC0FF', '#FF5765'];

/**
 * Full-screen 8-bit winner celebration.
 * kind: 'tournament' (shows a team + flag) | 'league' (shows a player).
 */
export default function WinnerCelebration({ kind, name, team, onClose }) {
  const [muted, setMuted] = useState(true);
  const { start, stop } = useChiptune();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleSound = () => {
    if (muted) { start(); setMuted(false); } else { stop(); setMuted(true); }
  };

  const kicker = kind === 'tournament' ? 'FIFA World Cup 2026' : 'Prediction League';
  const title = kind === 'tournament' ? 'CHAMPIONS' : 'LEAGUE CHAMPION';

  return (
    <div className="celebrate-backdrop" onClick={onClose} role="dialog" aria-label={`${title}: ${name}`}>
      <div className="celebrate-confetti" aria-hidden="true">
        {Array.from({ length: 48 }).map((_, i) => (
          <span key={i} className="px-confetti" style={{
            left: `${(i * 31) % 100}%`,
            animationDelay: `${((i % 12) * 0.18).toFixed(2)}s`,
            animationDuration: `${2.4 + (i % 5) * 0.4}s`,
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          }} />
        ))}
      </div>
      <div className="celebrate-card" onClick={(e) => e.stopPropagation()}>
        <div className="celebrate-kicker">{kicker}</div>
        <PixelTrophy />
        <div className="celebrate-title">{title}</div>
        <div className="celebrate-winner">
          {team && <Flag team={team} size="lg" />}
          <span className="celebrate-name">{name}</span>
        </div>
        <div className="celebrate-actions">
          <button className="btn btn-sm" onClick={toggleSound}>{muted ? '♪ Sound on' : '♪ Mute'}</button>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
